"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  formatPolynomial,
  formatRationalNice,
  isZeroR,
  type Rational,
  signR,
  subR,
} from "@/lib/tool-kit/math/rational";
import {
  firstTerms,
  fitTwoTerms,
  nthTerm,
  positionOf,
  sumOfTerms,
} from "@/lib/tool-kit/number-theory/arithmetic-sequence";
import {
  type RationalRead,
  readCountField,
  readRationalField,
} from "@/lib/tool-kit/number-theory/read";

/** English strings. A translated content file overrides these through `ui`. */
export const ARITHMETIC_SEQUENCE_CALCULATOR_STRINGS = {
  mode: "What do you know?",
  modeFirst: "First term and common difference",
  modeTwo: "Two terms of the sequence",
  modePosition: "Find where a value sits",
  firstTerm: "First term, a₁",
  difference: "Common difference, d",
  differenceHint: "Add this each time. Negative for a decreasing sequence.",
  termNumber: "Term number, n",
  termNumberHint: "A whole number from 1 up to 10¹⁵.",
  positionP: "Position of the first known term",
  valueP: "Its value",
  positionQ: "Position of the second known term",
  valueQ: "Its value",
  target: "Value to look for",
  results: "Results",
  nthTermCard: "Term a{ns}",
  sumCard: "Sum S{ns} of the first {n} terms",
  differenceCard: "Common difference",
  firstCard: "First term",
  formulaHeading: "General formula",
  generalTerm: "aₙ = {formula}",
  stepsHeading: "Working",
  stepDifference:
    "d = (a{qs} − a{ps}) ÷ ({q} − {p}) = ({y} − {xb}) ÷ ({q} − {p}) = {d}",
  stepFirst: "a₁ = a{ps} − ({p} − 1)d = {x} − ({p} − 1) × {db} = {a1}",
  stepTerm: "a{ns} = a₁ + ({n} − 1)d = {a1} + ({n} − 1) × {db} = {an}",
  stepSum: "S{ns} = {n}(a₁ + a{ns}) ÷ 2 = {n} × ({a1} + {anb}) ÷ 2 = {sum}",
  termsHeading: "The sequence",
  termsEllipsis: "…",
  increasing: "d is positive, so the sequence increases.",
  decreasing: "d is negative, so the sequence decreases.",
  constant: "d is 0, so every term equals the first.",
  positionFound:
    "{target} is term number {n}: n = ({target} − {a1}) ÷ {d} + 1 = {n}.",
  positionNot:
    "{target} is not a term. The number of steps from {a1} to {target}, ({target} − {a1}) ÷ {d}, is not a whole number of at least 0, so the sequence never lands on it.",
  positionEvery:
    "Every term equals {target}, so it is term 1, 2, 3, and so on.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "a{ns} = {an}; S{ns} = {sum}",
  enter: "Fill in every field to see the result.",
  invalid: "Enter numbers, decimals or fractions like 3/4.",
  tooLong: "Use numbers of at most 30 digits.",
  badN: "The term number must be a whole number from 1 to 10¹⁵.",
  samePosition: "The two known terms must be at different positions.",
  badPosition: "Positions must be whole numbers from 1 to 10¹⁵.",
} as const;

type Strings = typeof ARITHMETIC_SEQUENCE_CALCULATOR_STRINGS;
type Mode = "first" | "two" | "position";
type Show = (value: Rational) => string;

const MAX_POSITION = BigInt(10) ** BigInt(15);
const ONE = BigInt(1);
const LISTED_TERMS = 8;
const SUBSCRIPT_DIGITS = "₀₁₂₃₄₅₆₇₈₉";
const MONO_LINES =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";
const PROSE_SM = "text-muted-foreground text-sm";

/** 20 → "₂₀": a term label that reads as a subscript in any locale. */
function subscript(n: bigint | number): string {
  return [...String(n)].map((d) => SUBSCRIPT_DIGITS[Number(d)]).join("");
}

function decimalMarkFor(locale: string): string {
  const part = new Intl.NumberFormat(locale)
    .formatToParts(1.1)
    .find((p) => p.type === "decimal");
  return part?.value ?? ".";
}

function rationalError(
  reads: readonly RationalRead[],
  strings: Strings
): string | null {
  if (reads.some((r) => r.kind === "too-long")) {
    return strings.tooLong;
  }
  if (reads.some((r) => r.kind === "invalid")) {
    return strings.invalid;
  }
  return null;
}

/** A negative or fractional value in brackets, so "× −1/3" reads "× (−1/3)". */
function bracketed(value: Rational, show: Show): string {
  const text = show(value);
  return text.startsWith("−") || text.includes("/") ? `(${text})` : text;
}

function trendText(direction: number, strings: Strings): string {
  if (direction === 0) {
    return strings.constant;
  }
  return direction > 0 ? strings.increasing : strings.decreasing;
}

function Terms({
  a1,
  d,
  n,
  show,
  strings,
}: {
  a1: Rational;
  d: Rational;
  n: bigint;
  show: Show;
  strings: Strings;
}) {
  const count = n < BigInt(LISTED_TERMS) ? Number(n) : LISTED_TERMS;
  const head = firstTerms(a1, d, count).map(show);
  const long = n > BigInt(LISTED_TERMS);
  const last = show(nthTerm(a1, d, n));
  const text = long
    ? `${head.join(", ")}, ${strings.termsEllipsis}, ${last}`
    : head.join(", ");
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.termsHeading}</h3>
      <p className="break-words rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        {text}
      </p>
    </>
  );
}

function Results({
  a1,
  d,
  n,
  working,
  show,
  strings,
}: {
  a1: Rational;
  d: Rational;
  n: bigint;
  working: string[];
  show: Show;
  strings: Strings;
}) {
  const an = nthTerm(a1, d, n);
  const sum = sumOfTerms(a1, d, n);
  const sub = subscript(n);
  const values = {
    ns: sub,
    n: String(n),
    a1: show(a1),
    d: show(d),
    db: bracketed(d, show),
    an: show(an),
    anb: bracketed(an, show),
    sum: show(sum),
  };
  const direction = signR(d);
  const trend = trendText(direction, strings);
  const formula = formatPolynomial(
    [
      { coefficient: d, variable: "n" },
      { coefficient: subR(a1, d), variable: "" },
    ],
    "fraction",
    "."
  );
  const summary = t(strings.summary, values);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.nthTermCard, values)}
          value={values.an}
        />
        <StatCard label={t(strings.sumCard, values)} value={values.sum} />
        <StatCard label={strings.differenceCard} value={values.d} />
        <StatCard label={strings.firstCard} value={values.a1} />
      </StatGrid>
      <p className={PROSE_SM}>{trend}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
      <h3 className={WIDGET_H3}>{strings.formulaHeading}</h3>
      <div className={MONO_LINES}>
        <p>{t(strings.generalTerm, { formula })}</p>
      </div>
      <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
      <div className={MONO_LINES}>
        {working.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{t(strings.stepTerm, values)}</p>
        <p>{t(strings.stepSum, values)}</p>
      </div>
      <Terms a1={a1} d={d} n={n} show={show} strings={strings} />
    </>
  );
}

function FirstMode({
  rawA1,
  rawD,
  rawN,
  show,
  strings,
}: {
  rawA1: string;
  rawD: string;
  rawN: string;
  show: Show;
  strings: Strings;
}) {
  const a1 = readRationalField(rawA1);
  const d = readRationalField(rawD);
  const n = readCountField(rawN, ONE, MAX_POSITION);
  const error = rationalError([a1, d], strings);
  if (error) {
    return <ErrorNotice>{error}</ErrorNotice>;
  }
  if (a1.kind !== "ok" || d.kind !== "ok" || n.kind === "empty") {
    return <Notice>{strings.enter}</Notice>;
  }
  if (n.kind !== "ok") {
    return <ErrorNotice>{strings.badN}</ErrorNotice>;
  }
  return (
    <Results
      a1={a1.value}
      d={d.value}
      n={n.value}
      show={show}
      strings={strings}
      working={[]}
    />
  );
}

function TwoMode({
  fields,
  rawN,
  show,
  strings,
}: {
  fields: { p: string; x: string; q: string; y: string };
  rawN: string;
  show: Show;
  strings: Strings;
}) {
  const p = readCountField(fields.p, ONE, MAX_POSITION);
  const q = readCountField(fields.q, ONE, MAX_POSITION);
  const x = readRationalField(fields.x);
  const y = readRationalField(fields.y);
  const n = readCountField(rawN, ONE, MAX_POSITION);
  const error = rationalError([x, y], strings);
  const fit =
    p.kind === "ok" && q.kind === "ok" && x.kind === "ok" && y.kind === "ok"
      ? fitTwoTerms(p.value, x.value, q.value, y.value)
      : null;
  const anyEmpty = [p, q, x, y, n].some((r) => r.kind === "empty");
  if (error) {
    return <ErrorNotice>{error}</ErrorNotice>;
  }
  if (anyEmpty) {
    return <Notice>{strings.enter}</Notice>;
  }
  if (p.kind !== "ok" || q.kind !== "ok") {
    return <ErrorNotice>{strings.badPosition}</ErrorNotice>;
  }
  if (n.kind !== "ok") {
    return <ErrorNotice>{strings.badN}</ErrorNotice>;
  }
  if (fit === null || x.kind !== "ok" || y.kind !== "ok") {
    return <ErrorNotice>{strings.samePosition}</ErrorNotice>;
  }
  const values = {
    ps: subscript(p.value),
    qs: subscript(q.value),
    p: String(p.value),
    q: String(q.value),
    x: show(x.value),
    xb: bracketed(x.value, show),
    y: show(y.value),
    d: show(fit.d),
    db: bracketed(fit.d, show),
    a1: show(fit.a1),
  };
  return (
    <Results
      a1={fit.a1}
      d={fit.d}
      n={n.value}
      show={show}
      strings={strings}
      working={[
        t(strings.stepDifference, values),
        t(strings.stepFirst, values),
      ]}
    />
  );
}

function PositionMode({
  raw,
  show,
  strings,
}: {
  raw: { a1: string; d: string; target: string };
  show: Show;
  strings: Strings;
}) {
  const a1 = readRationalField(raw.a1);
  const d = readRationalField(raw.d);
  const target = readRationalField(raw.target);
  const error = rationalError([a1, d, target], strings);
  if (error) {
    return <ErrorNotice>{error}</ErrorNotice>;
  }
  if (a1.kind !== "ok" || d.kind !== "ok" || target.kind !== "ok") {
    return <Notice>{strings.enter}</Notice>;
  }
  const found = positionOf(a1.value, d.value, target.value);
  const values = {
    target: show(target.value),
    a1: show(a1.value),
    d: show(d.value),
    n: found.kind === "term" ? String(found.n) : "",
  };
  if (found.kind === "term") {
    return (
      <>
        <StatCard emphasis label={values.target} value={values.n} />
        <p className="text-foreground text-sm">
          {t(strings.positionFound, values)}
        </p>
        {isZeroR(d.value) ? null : (
          <Results
            a1={a1.value}
            d={d.value}
            n={found.n}
            show={show}
            strings={strings}
            working={[]}
          />
        )}
      </>
    );
  }
  return (
    <Notice>
      {found.kind === "every-term"
        ? t(strings.positionEvery, values)
        : t(strings.positionNot, values)}
    </Notice>
  );
}

export default function ArithmeticSequenceCalculatorWidget() {
  const strings = useToolUi(ARITHMETIC_SEQUENCE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const mark = useMemo(() => decimalMarkFor(locale), [locale]);
  const show: Show = (value) => formatRationalNice(value, mark);
  const [mode, setMode] = useState<Mode>("first");
  const [a1, setA1] = useState("3");
  const [d, setD] = useState("4");
  const [n, setN] = useState("20");
  const [p, setP] = useState("3");
  const [x, setX] = useState("11");
  const [q, setQ] = useState("8");
  const [y, setY] = useState("36");
  const [target, setTarget] = useState("79");
  return (
    <>
      <PillGroup
        label={strings.mode}
        name="asc-mode"
        onChange={setMode}
        options={[
          { value: "first", label: strings.modeFirst },
          { value: "two", label: strings.modeTwo },
          { value: "position", label: strings.modePosition },
        ]}
        value={mode}
      />
      {mode === "two" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="asc-p"
            label={strings.positionP}
            mono
            numeric
            onChange={setP}
            value={p}
          />
          <TextField
            id="asc-x"
            label={strings.valueP}
            mono
            numeric
            onChange={setX}
            value={x}
          />
          <TextField
            id="asc-q"
            label={strings.positionQ}
            mono
            numeric
            onChange={setQ}
            value={q}
          />
          <TextField
            id="asc-y"
            label={strings.valueQ}
            mono
            numeric
            onChange={setY}
            value={y}
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="asc-a1"
            label={strings.firstTerm}
            mono
            numeric
            onChange={setA1}
            value={a1}
          />
          <TextField
            hint={strings.differenceHint}
            id="asc-d"
            label={strings.difference}
            mono
            numeric
            onChange={setD}
            value={d}
          />
        </div>
      )}
      {mode === "position" ? (
        <TextField
          id="asc-target"
          label={strings.target}
          mono
          numeric
          onChange={setTarget}
          value={target}
        />
      ) : (
        <TextField
          hint={strings.termNumberHint}
          id="asc-n"
          label={strings.termNumber}
          mono
          numeric
          onChange={setN}
          value={n}
        />
      )}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "first" ? (
          <FirstMode
            rawA1={a1}
            rawD={d}
            rawN={n}
            show={show}
            strings={strings}
          />
        ) : null}
        {mode === "two" ? (
          <TwoMode
            fields={{ p, x, q, y }}
            rawN={n}
            show={show}
            strings={strings}
          />
        ) : null}
        {mode === "position" ? (
          <PositionMode raw={{ a1, d, target }} show={show} strings={strings} />
        ) : null}
      </section>
    </>
  );
}
